import type { UsageBillingSummaryItem } from "./usage-billing-format";

const CENTS_PER_UNIT = 100;
const CURRENCY_DECIMAL_PLACES = 2;

export type UsageBillingOrgGroup = {
  organizationId: string;
  organizationName: string;
  units: UsageBillingSummaryItem[];
};

// Units arrive sorted by (organization name, organization id, unit name, unit
// id), so consecutive same-org units collapse into one group and two
// organizations sharing a name never interleave. This tolerates an
// organization's units spanning a page boundary: re-running the fold over the
// full loaded list after fetchNextPage appends new units to the existing last
// group instead of creating a duplicate header.
export function groupUnitsByOrganization(
  units: UsageBillingSummaryItem[],
): UsageBillingOrgGroup[] {
  const groups: UsageBillingOrgGroup[] = [];

  for (const unit of units) {
    const lastGroup = groups.at(-1);
    if (lastGroup?.organizationId === unit.organizationId) {
      lastGroup.units.push(unit);
    } else {
      groups.push({
        organizationId: unit.organizationId,
        organizationName: unit.organizationName,
        units: [unit],
      });
    }
  }

  return groups;
}

// Sum in integer cents; accumulating the decimal strings as floats drifts.
export function sumUnitTotalAmounts(units: UsageBillingSummaryItem[]): string {
  const totalCents = units.reduce(
    (sum, unit) =>
      sum + Math.round(Number(unit.summary.totalAmount) * CENTS_PER_UNIT),
    0,
  );
  return (totalCents / CENTS_PER_UNIT).toFixed(CURRENCY_DECIMAL_PLACES);
}
